import { useLazyComment } from "@/hooks/useComment";
import {
  useCollectorMetaCountAndLike,
  useMetaLikeAndComment,
} from "@/hooks/useMeta";
import useTranslation from "@/hooks/useTranslation";
import { SearchContentListItem } from "@/interfaces/pubmed.interface";
import { showCollectorCommentsState } from "@/stores/collection";
import { currentUserState } from "@/stores/common";
import { pubmedState } from "@/stores/pubmed";
import { Tabs } from "antd";
import { css } from "emotion";
import React, { useMemo } from "react";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { useRecoilValue } from "recoil";
import Abstract from "./Abstract";
import AddComment from "./AddComment";
import AllComment from "./AllComment";
import CollectorComment from "./CollectorComment";
import Helper from "./Helper";
import styles from "./styles.module.less";
import UserComment from "./UserComment";

const { container } = styles;

const { TabPane } = Tabs;

interface IProps {
  fetchResultsEach: SearchContentListItem;
}

const Tab: React.FC<IProps> = (props) => {
  const { fetchResultsEach } = props;
  const {
    displayPreference: { defaultActiveKey, verbose, immersiveMode },
  } = useRecoilValue(pubmedState);

  const {
    keywords,
    abstract,
    eDoi,
    articleIds,
    ePmid,
    articleTitle,
  } = fetchResultsEach;

  const { pmid, doi } = useMemo(() => {
    const aPmid = articleIds.find((e) => e?.idType?.toLowerCase() === "pubmed")
      ?.id;
    const aDoi = articleIds.find((e) => e?.idType?.toLowerCase() === "doi")?.id;
    return {
      pmid: ePmid || aPmid || "",
      doi: eDoi || aDoi || "",
    };
  }, [articleIds, eDoi, ePmid]);

  const { t } = useTranslation("explore");

  const showCollectorComments = useRecoilValue(showCollectorCommentsState);

  const {
    getComments: getAllComments,
    loadingGet: loadingAll,
  } = useLazyComment(pmid, "all");

  const {
    getComments: getUserComments,
    loadingGet: loadingUser,
  } = useLazyComment(pmid, "user");

  const {
    getComments: getCollectorComments,
    loadingGet: loadingCollector,
  } = useLazyComment(pmid, "collector");

  const handleChange = (key: string) => {
    if (key === "AllComment") {
      getAllComments();
    } else if (key === "MyComment") {
      getUserComments();
    } else if (key === "CollectorComment") {
      getCollectorComments();
    }
  };

  const currentUser = useRecoilValue(currentUserState);
  const { commentTimeCount, myCommentTimeCount } = useMetaLikeAndComment(pmid);

  const { collectorCommentTime } = useCollectorMetaCountAndLike(pmid);

  return (
    <SwitchTransition>
      <CSSTransition
        key={defaultActiveKey}
        timeout={500}
        classNames="fade"
        unmountOnExit
      >
        <div
          className={`${css`
            max-height: ${verbose ? "200rem" : "0"};
            overflow: hidden;
            opacity: ${verbose ? "1" : "0"};
            transition: max-height 1s ease-in-out, opacity 1s ease-in-out;
            .ant-tabs-top > .ant-tabs-nav .ant-tabs-nav-wrap::after,
            .ant-tabs-bottom > .ant-tabs-nav .ant-tabs-nav-wrap::after,
            .ant-tabs-top > div > .ant-tabs-nav .ant-tabs-nav-wrap::after,
            .ant-tabs-bottom > div > .ant-tabs-nav .ant-tabs-nav-wrap::after {
              box-shadow: unset;
            }
            .ant-tabs-top > .ant-tabs-nav .ant-tabs-nav-wrap::before,
            .ant-tabs-bottom > .ant-tabs-nav .ant-tabs-nav-wrap::before,
            .ant-tabs-top > div > .ant-tabs-nav .ant-tabs-nav-wrap::before,
            .ant-tabs-bottom > div > .ant-tabs-nav .ant-tabs-nav-wrap::before {
              box-shadow: unset;
            }
          `}
            ${styles.left}
          `}
        >
          <Tabs
            defaultActiveKey={defaultActiveKey}
            animated={false}
            className={container}
            key={defaultActiveKey}
            size="large"
            onChange={handleChange}
          >
            <TabPane
              forceRender
              tab={
                <span style={{ fontSize: "14px" }}>
                  {t("tab.helper.itself")}
                </span>
              }
              key="Helper"
            >
              <Helper doi={doi} pmid={pmid} articleTitle={articleTitle} />
            </TabPane>
            <TabPane
              forceRender
              tab={
                <span style={{ fontSize: "0.875rem" }}>
                  {t("tab.abstract.itself")}
                </span>
              }
              key="Abstract"
            >
              <Abstract
                keywords={keywords}
                abstract={abstract}
                immersiveMode={immersiveMode}
                verbose={verbose}
                ePmid={ePmid}
              />
            </TabPane>
            {showCollectorComments.show && (
              <TabPane
                tab={
                  <span style={{ fontSize: "0.875rem" }}>
                    {t("tab.comment.collector")}
                    <sup className={styles.color}>
                      {Boolean(collectorCommentTime) && collectorCommentTime}
                    </sup>
                  </span>
                }
                key="CollectorComment"
              >
                <CollectorComment fatherId={pmid} loading={loadingCollector} />
              </TabPane>
            )}
            {currentUser?.id && (
              <TabPane
                tab={
                  <span style={{ fontSize: "0.875rem" }}>
                    {t("tab.comment.my")}
                    <sup className={styles.color}>
                      {Boolean(myCommentTimeCount) && myCommentTimeCount}
                    </sup>
                  </span>
                }
                key="MyComment"
              >
                <UserComment fatherId={pmid} loading={loadingUser} />
              </TabPane>
            )}
            <TabPane
              tab={
                <span style={{ fontSize: "0.875rem" }}>
                  {t("tab.comment.all")}
                  <sup className={styles.color}>
                    {Boolean(commentTimeCount) && commentTimeCount}
                  </sup>
                </span>
              }
              key="AllComment"
            >
              <AllComment fatherId={pmid} loading={loadingAll} />
            </TabPane>
            {currentUser?.id && (
              <TabPane
                tab={
                  <span style={{ fontSize: "0.875rem" }}>
                    {t("common:comment.add")}
                  </span>
                }
                key="AddComment"
              >
                <AddComment fatherId={pmid} url={`/navigator/paper/${pmid}`} />
              </TabPane>
            )}
          </Tabs>
        </div>
      </CSSTransition>
    </SwitchTransition>
  );
};

export default Tab;
